import {
  Box,
  Divider,
  IconButton,
  IconButtonProps,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  PopoverProps,
} from "@mui/material";
import { ReactNode, useState } from "react";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { UserRole } from "../../services/swagger";
import useRole from "../../hooks/useRole";

export interface Option {
  icon?: any;
  label?: string;
  divider?: boolean;
  renderItem?: ReactNode;
  onClick?: () => any;
  closeOnClick?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  role?: UserRole;
}

interface OptionsButtonProps {
  icon?: ReactNode;
  iconButtonProps?: IconButtonProps;
  popoverProps?: Partial<PopoverProps>;
  options: Option[];
  onClose?: () => any;
  onOpen?: () => any;
}

export default function OptionsButton(props: OptionsButtonProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const checkRole = useRole();

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    props.onOpen?.();
  };

  const handleClose = () => {
    setAnchorEl(null);
    props.onClose?.();
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton onClick={handleOpen} {...props.iconButtonProps}>
        {props.icon ?? <MoreHorizIcon />}
      </IconButton>
      <Popover
        open={props.popoverProps?.open ?? open}
        anchorEl={props.popoverProps?.anchorEl ?? anchorEl}
        onClose={props.popoverProps?.onClose ?? handleClose}
        anchorOrigin={
          props.popoverProps?.anchorOrigin ?? {
            vertical: "bottom",
            horizontal: "center",
          }
        }
        transformOrigin={
          props.popoverProps?.transformOrigin ?? {
            vertical: "top",
            horizontal: "center",
          }
        }
        sx={{ mt: 1, ...props.popoverProps?.sx }}
        PaperProps={{
          sx: { minWidth: 200, py: 0.5, ...props.popoverProps?.PaperProps?.sx },
          ...props.popoverProps?.PaperProps,
        }}
        {...props.popoverProps}
      >
        {props.options.map((option, i) => {
          if (option.hidden || (option.role && !checkRole(option.role)))
            return null;
          else if (option.renderItem)
            return (
              <Box position="relative" key={i}>
                {option.renderItem}
              </Box>
            );
          else if (option.divider) return <Divider key={i} />;
          else
            return (
              <MenuItem
                key={i}
                onClick={() => {
                  if (option.closeOnClick) handleClose();
                  option.onClick?.();
                  setAnchorEl(null);
                }}
                disabled={option.disabled}
              >
                {option.icon && <ListItemIcon>{option.icon}</ListItemIcon>}
                {option.label && <ListItemText>{option.label}</ListItemText>}
              </MenuItem>
            );
        })}
      </Popover>
    </>
  );
}
